import { notEmpty } from '@ember/object/computed';
import { computed } from '@ember/object';
import { isNone } from '@ember/utils';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { belongsTo } from 'ember-data/relationships';
import { queryEncode, queryDecode } from 'cio/helpers/query';
import UserOtherAccountModel from 'cio/models/user-other-account';
import EnvironmentModel from 'cio/models/environment';
import { SerializedLegacyFilters } from 'cio/types/conditions/legacy-filter';
import { Grouping } from 'cio/utils/metrics/resolution';

const KNOWN_EXPORT_TYPES = ['delivery_totals', 'deliveries', 'customers', 'segment_membership'];

export default class ExportModel extends Model {
  @attr('string') description?: string;
  @attr('number') done!: number;
  @attr('number') downloads?: number;
  @attr('boolean') failed?: boolean;
  @attr() options?: {
    [key: string]: any;
    api_filters?: string;
    filters?: SerializedLegacyFilters;
    resolution?: Grouping;
    segment_id: number;
    start?: number;
    end?: number;
    email?: string;
  };
  @attr('string') path?: string;
  @attr('number') total!: number;
  @attr('string') type!: string;
  @attr('number') createdAt!: number;
  @attr('number') updatedAt?: number;

  @belongsTo('user-other-account', { async: false, inverse: 'exports' }) user!: UserOtherAccountModel;
  @belongsTo('environment', { async: true, inverse: null }) environment!: EnvironmentModel;

  @notEmpty('path') isComplete!: boolean;

  @computed('done', 'total')
  get progress() {
    let { done, total } = this;

    if (total > 0) {
      return Math.round((done / total) * 100);
    } else {
      return 0;
    }
  }

  // All the methods below are used to build the link
  @computed('options.{filters,api_filters}')
  get hasFilters() {
    return !isNone(this.options?.filters) || !isNone(this.options?.api_filters);
  }

  @computed('options.email')
  get hasEmailSearch() {
    return !isNone(this.options?.email);
  }

  @computed('type', 'environment')
  get hasLink() {
    return !isNone(this.environment) && KNOWN_EXPORT_TYPES.includes(this.type);
  }

  @computed('type', 'options', 'hasLink')
  // eslint-disable-next-line getter-return
  get linkType() {
    if (this.hasLink) {
      let { type: exportType, options } = this;

      if (exportType === 'delivery_totals') {
        if (options?.campaign_ids || options?.campaign_id || options?.campaign) {
          return 'campaigns.campaign';
        } else if (options?.newsletter_ids || options?.newsletter_id || options?.newsletter) {
          if (options?.newsletter_ids && options?.newsletter_ids.length > 1) {
            return 'broadcasts.list.newsletters.index';
          } else {
            return 'broadcasts.newsletter';
          }
        }
      } else if (exportType === 'deliveries') {
        // When an export is created from drafts, the exportType is still deliveries,
        // but the filters will contain "drafted".
        if (options?.and_filters?.includes('drafted')) {
          return 'outbox.drafts';
        } else {
          return 'outbox.deliveries';
        }
      } else if (exportType === 'customers') {
        return 'people';
      } else if (exportType === 'segment_membership') {
        return 'segments.segment';
      }
    }

    return;
  }

  @computed('options.filters', 'options.api_filters')
  get encodedFilters() {
    // New filter syntax is stored on the back end as a b64 string already, so we don't need to encode it
    // It does, however, come back as an unencoded b64 json string. So we need to decode and reencode it properly
    if (this.options?.api_filters) {
      return queryEncode(queryDecode(this.options.api_filters));
    }

    return queryEncode(this.options?.filters ?? []);
  }

  @computed('linkType', '_campaignId', '_newsletterId')
  // eslint-disable-next-line getter-return
  get linkId() {
    let { linkType } = this;

    if (linkType === 'campaigns.campaign') {
      return this._campaignId;
    } else if (linkType === 'broadcasts.newsletter') {
      return this._newsletterId;
    } else if (linkType === 'segments.segment') {
      return this.options?.segment_id;
    }

    return;
  }

  @computed('options')
  get _campaignId() {
    let { options } = this;

    if (options?.campaign_ids) {
      return options.campaign_ids.firstObject;
    } else {
      return options?.campaign || options?.campaign_id;
    }
  }

  @computed('options')
  get _newsletterId() {
    let { options } = this;

    if (options?.newsletter_ids) {
      return options.newsletter_ids.firstObject;
    } else {
      return options?.newsletter || options?.newsletter_id;
    }
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    export: ExportModel;
  }
}
